import axios from "axios";

/**
 * Resolve the API base URL with a defensive fallback.
 *
 * Build-time env REACT_APP_BACKEND_URL is the source of truth, but if it
 * points at a DIFFERENT origin than the page is currently loaded from
 * (e.g. user is on https://moodcard.app but env baked in https://feel-to-card.emergent.host),
 * we fall back to the page origin. This guarantees same-origin requests
 * (no CORS preflight, no credentialed-wildcard pitfalls) whenever the
 * frontend is served from the same domain as the backend (which is the
 * case for every Emergent deploy on a custom domain).
 */
const _envBackend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const _samePageOrigin = typeof window !== "undefined" ? window.location.origin : "";

function _resolveBackend() {
    if (!_envBackend) return _samePageOrigin;       // dev with no env → use current origin
    if (!_samePageOrigin) return _envBackend;       // SSR / build context
    try {
        const envHost = new URL(_envBackend).host;
        const pageHost = new URL(_samePageOrigin).host;
        // Same origin → no point doing cross-origin requests. Use page origin.
        if (envHost === pageHost) return _samePageOrigin;
        // Production sanity: if the page is on a stable public host (https) and the env
        // points to an emergent.* preview/host, prefer the page origin so cookies + CORS Just Work.
        if (
            _samePageOrigin.startsWith("https://") &&
            /\.(emergent\.host|emergentagent\.com)$/i.test(envHost) &&
            !/\.(emergent\.host|emergentagent\.com)$/i.test(pageHost)
        ) {
            return _samePageOrigin;
        }
    } catch { /* fall through */ }
    return _envBackend;
}

const BACKEND_URL = _resolveBackend();
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export const createMoodCard = async (feeling, pack = "cinematic", personalize = null, use_ai = true, ref_code = null, reply_to_slug = null) => {
    const { data } = await axios.post(`${API}/moodcards`, { feeling, pack, personalize, use_ai, ref_code, reply_to_slug });
    return data;
};

export const regenerateMoodCard = async (id, body = null) => {
    const { data } = await axios.post(`${API}/moodcards/${id}/regenerate`, body || undefined);
    return data;
};

// iter-72 (e): revert cards to a previous version snapshot.
// `index` is the 0-based position inside the moodcard's `versions[]` array.
export const revertMoodCard = async (id, index) => {
    const { data } = await axios.post(`${API}/moodcards/${id}/revert`, { index });
    return data;
};

// iter-72 (e): MoodCard Challenges
export const listChallenges = async () => {
    const { data } = await axios.get(`${API}/challenges`);
    return data.items || [];
};

export const getChallenge = async (slug) => {
    const { data } = await axios.get(`${API}/challenges/${encodeURIComponent(slug)}`);
    return data;
};

export const joinChallenge = async (moodId, slug) => {
    const { data } = await axios.post(`${API}/moodcards/${moodId}/challenge`, { slug });
    return data;
};

export const switchPack = async (id, pack) => {
    const { data } = await axios.post(`${API}/moodcards/${id}/pack`, { pack });
    return data;
};

export const getMoodCard = async (id) => {
    const { data } = await axios.get(`${API}/moodcards/${id}`);
    return data;
};

export const selectStyle = async (id, style) => {
    const { data } = await axios.post(`${API}/moodcards/${id}/select`, { style });
    return data;
};

export const getMyMoodcards = async (limit = 50) => {
    const { data } = await axios.get(`${API}/moodcards/mine/list?limit=${limit}`, { withCredentials: true });
    return data.items || [];
};

export const getMyQuota = async () => {
    const { data } = await axios.get(`${API}/me/quota`);
    return data; // { tier, used, limit, remaining }
};

export const mintShareSlug = async (id) => {
    const { data } = await axios.post(`${API}/moodcards/${id}/share-slug`);
    return data; // { slug, share_url, remix_url }
};

export const inferPersonalize = async (text) => {
    // Returns { suggestion: { lane, subMood, temperature, occasion, relationship, confidence } | null }
    const { data } = await axios.post(`${API}/personalize/infer`, { text });
    return data;
};

export const validateExport = async (id) => {
    const { data } = await axios.post(`${API}/moodcards/export/validate`, { mood_id: id });
    return data; // { watermark_required, tier }
};

/**
 * Soft micro-reply ping. Fired by PublicCard on mount when ?to= is present.
 *
 * • viewer_id is an anonymous browser UUID stored in localStorage so the
 *   same person reloading the link doesn't inflate the open count.
 * • Failures are intentionally silent — open-pings are a "nice to have"
 *   loop, not a critical write. Missing one shouldn't disrupt the view.
 */
export const recordCardOpen = async (slug, recipient, viewerId) => {
    try {
        const { data } = await axios.post(`${API}/cards/${slug}/opens`, {
            viewer_id: viewerId,
            recipient: recipient || null,
        });
        return data;
    } catch (e) {
        // swallow — not critical
        return null;
    }
};

export const getMyCardOpens = async (limit = 20) => {
    const { data } = await axios.get(`${API}/me/card-opens?limit=${limit}`, { withCredentials: true });
    return data.items || [];
};

/**
 * Quiet correspondence — the ancestor chain ending at this mood_id.
 * Public read. Each entry exposes ONLY: id, slug, feeling, style,
 * created_at, sender_name (first name only). No cards body, no surnames.
 */
export const getThread = async (moodId) => {
    const { data } = await axios.get(`${API}/cards/${moodId}/thread`);
    return data; // { thread: [...], count }
};

/**
 * Public gallery — recent shareable cards from named senders.
 * Anonymous read. First-name-only attribution.
 */
export const getPublicExplore = async (limit = 24, offset = 0, lane = null) => {
    const laneParam = lane ? `&lane=${encodeURIComponent(lane)}` : "";
    const { data } = await axios.get(`${API}/public/explore?limit=${limit}&offset=${offset}${laneParam}`);
    return data; // { items: [...], count, offset }
};

/**
 * Card of the Day — one card to feature on Home, refreshed daily.
 * Anonymous read. Returns {date, card, slug, author_first_name, ...}
 * or {date, card: null} when nothing qualifies.
 */
export const getCardOfTheDay = async () => {
    const { data } = await axios.get(`${API}/public/card-of-the-day`);
    return data;
};


/**
 * Custom user-uploaded background (Premium feature).
 * Returns { mood_id, url, size } on success. Throws with .response.status
 * set to 402 when caller is not Premium, 413 when image > 5MB, 400 for
 * bad type / empty file, 403 for non-owner.
 */
export const uploadCustomBackground = async (moodId, file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await axios.post(`${API}/cards/${moodId}/custom-background`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
};

export const removeCustomBackground = async (moodId) => {
    const { data } = await axios.delete(`${API}/cards/${moodId}/custom-background`, { withCredentials: true });
    return data;
};

/**
 * Signature backdrop — Premium creator's default photo that auto-applies
 * to every NEW card they create. Editing it doesn't mutate prior cards.
 */
export const getDefaultBackground = async () => {
    const { data } = await axios.get(`${API}/users/me/default-background`, { withCredentials: true });
    return data;  // { url: string|null }
};

export const uploadDefaultBackground = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await axios.post(`${API}/users/me/default-background`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
};

export const removeDefaultBackground = async () => {
    const { data } = await axios.delete(`${API}/users/me/default-background`, { withCredentials: true });
    return data;
};

/**
 * Creators gallery — Premium users opted in for /creators discovery.
 */
export const getCreatorVisibility = async () => {
    const { data } = await axios.get(`${API}/users/me/creator-profile`, { withCredentials: true });
    return data; // { profile_public: bool }
};

/**
 * Updates a partial creator profile — pass only fields you want to change.
 */
export const setCreatorVisibility = async (profile_public) => {
    const { data } = await axios.post(`${API}/users/me/creator-profile`, { profile_public }, { withCredentials: true });
    return data;
};

export const listCreators = async (limit = 24, lane = null) => {
    const params = { limit };
    if (lane) params.lane = lane;
    const { data } = await axios.get(`${API}/creators`, { params });
    return data; // { items, count }
};

export const getCreatorLaneCounts = async () => {
    const { data } = await axios.get(`${API}/creators/lane-counts`);
    return data; // { counts: {lane: n}, total }
};

export const getCreatorProfile = async (creatorId, limit = 12) => {
    const { data } = await axios.get(`${API}/creators/${creatorId}`, { params: { limit } });
    return data; // { creator_id, name, signature_backdrop_url, recent_cards, total_cards }
};

/**
 * Updates a partial creator profile — pass only fields you want to change.
 * { profile_public?, signature_line?, qr_destination?, brand_colors? }
 */
export const updateCreatorProfile = async (patch) => {
    const { data } = await axios.post(`${API}/users/me/creator-profile`, patch, { withCredentials: true });
    return data;
};

export const uploadCreatorLogo = async (file) => {
    const form = new FormData();
    form.append("file", file);
    const { data } = await axios.post(`${API}/users/me/creator-logo`, form, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
};

export const removeCreatorLogo = async () => {
    const { data } = await axios.delete(`${API}/users/me/creator-logo`, { withCredentials: true });
    return data;
};
